import React, { useState, useRef, useEffect } from 'react';
import { Container, DateInput, Dropdown, Calendar, DateButton } from './styles';

export const DashboardDateRange = ( { value, onChange, label } ) => {
	const [ isOpen, setIsOpen ] = useState( false );
	const [ tempStartDate, setTempStartDate ] = useState( null );
	const [ tempEndDate, setTempEndDate ] = useState( null );
	const [ dropdownPosition, setDropdownPosition ] = useState( { top: 0, left: 0, width: 0 } );
	const [ currentYear, setCurrentYear ] = useState( new Date().getFullYear() );
	const [ currentMonth, setCurrentMonth ] = useState( new Date().getMonth() );
	const containerRef = useRef( null );

	// Formatar data para dd/mm/yyyy
	const formatDate = ( date ) => {
		if ( !date || !( date instanceof Date ) || isNaN( date.getTime() ) ) {
			return '';
		}
		const day = String( date.getDate() ).padStart( 2, '0' );
		const month = String( date.getMonth() + 1 ).padStart( 2, '0' );
		const year = date.getFullYear();
		return `${day}/${month}/${year}`;
	};

	// Obter valor de exibição
	const getDisplayValue = () => {
		if ( !value || !Array.isArray( value ) || value.length !== 2 ) {
			return 'Selecionar período';
		}
		
		const [ startDate, endDate ] = value;
		const formattedStart = formatDate( startDate );
		const formattedEnd = formatDate( endDate );
		
		if ( formattedStart && formattedEnd ) {
			return `${formattedStart} - ${formattedEnd}`;
		} else if ( formattedStart ) {
			return formattedStart;
		} else if ( formattedEnd ) {
			return formattedEnd;
		}
		
		return 'Selecionar período';
	};

	// Fechar dropdown ao clicar fora
	useEffect( () => {
		const handleClickOutside = ( event ) => {
			if ( containerRef.current && !containerRef.current.contains( event.target ) ) {
				setIsOpen( false );
				setTempStartDate( null );
				setTempEndDate( null );
			}
		};

		document.addEventListener( 'mousedown', handleClickOutside );
		return () => document.removeEventListener( 'mousedown', handleClickOutside );
	}, [] );

	// Gerar dias do calendário
	const generateCalendarDays = ( year, month ) => {
		const firstDay = new Date( year, month, 1 );
		const startDate = new Date( firstDay );
		startDate.setDate( startDate.getDate() - firstDay.getDay() );

		const days = [];
		const current = new Date( startDate );
		
		for ( let i = 0; i < 42; i++ ) {
			days.push( new Date( current ) );
			current.setDate( current.getDate() + 1 );
		}
		
		return days;
	};

	// Verificar se data está no range selecionado
	const isInRange = ( date ) => {
		if ( !tempStartDate || !tempEndDate ) return false;
		return date >= tempStartDate && date <= tempEndDate;
	};

	// Lidar com clique em data
	const handleDateClick = ( date ) => {
		if ( !tempStartDate || ( tempStartDate && tempEndDate ) ) {
			setTempStartDate( date );
			setTempEndDate( null );
		} else if ( date >= tempStartDate ) {
			setTempEndDate( date );
		} else {
			setTempStartDate( date );
			setTempEndDate( null );
		}
	};

	// Aplicar seleção
	const handleApply = () => {
		if ( tempStartDate && tempEndDate ) {
			onChange( [ tempStartDate, tempEndDate ] );
			setIsOpen( false );
			setTempStartDate( null );
			setTempEndDate( null );
		}
	};

	// Limpar seleção
	const handleClear = () => {
		onChange( null );
		setIsOpen( false );
		setTempStartDate( null );
		setTempEndDate( null );
	};

	// Calcular posição do dropdown
	const calculateDropdownPosition = () => {
		if ( containerRef.current ) {
			const rect = containerRef.current.getBoundingClientRect();
			setDropdownPosition( {
				top   : rect.bottom + window.scrollY + 4,
				left  : rect.left + window.scrollX,
				width : rect.width
			} );
		}
	};

	// Abrir/fechar dropdown
	const toggleDropdown = () => {
		if ( !isOpen ) {
			calculateDropdownPosition();
		}
		setIsOpen( !isOpen );
	};

	// Navegar para mês anterior
	const goToPreviousMonth = () => {
		if ( currentMonth === 0 ) {
			setCurrentMonth( 11 );
			setCurrentYear( currentYear - 1 );
		} else {
			setCurrentMonth( currentMonth - 1 );
		}
	};

	// Navegar para próximo mês
	const goToNextMonth = () => {
		if ( currentMonth === 11 ) {
			setCurrentMonth( 0 );
			setCurrentYear( currentYear + 1 );
		} else {
			setCurrentMonth( currentMonth + 1 );
		}
	};

	const calendarDays = generateCalendarDays( currentYear, currentMonth );
	const monthNames = [
		'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
		'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
	];

	return (
		<Container ref={containerRef}>
			{label && <label>{label}</label>}
			<DateInput onClick={toggleDropdown}>
				{getDisplayValue()}
			</DateInput>
			
			{isOpen && (
				<Dropdown style={{
					top      : `${dropdownPosition.top}px`,
					left     : `${dropdownPosition.left}px`,
					minWidth : `${dropdownPosition.width}px`
				}}>
					<Calendar>
						<div style={{ 
							display        : 'flex', 
							justifyContent : 'space-between', 
							alignItems     : 'center',
							marginBottom   : '16px',
							fontWeight     : '600',
							fontSize       : '16px'
						}}>
							<button
								onClick={goToPreviousMonth}
								style={{
									background     : 'none',
									border         : 'none',
									cursor         : 'pointer',
									fontSize       : '18px',
									color          : '#666',
									padding        : '4px 8px',
									borderRadius   : '4px',
									display        : 'flex',
									alignItems     : 'center',
									justifyContent : 'center'
								}}
								title="Mês anterior"
							>
								‹
							</button>
							
							<span>
								{monthNames[currentMonth]} {currentYear}
							</span>
							
							<button
								onClick={goToNextMonth}
								style={{
									background     : 'none',
									border         : 'none',
									cursor         : 'pointer',
									fontSize       : '18px',
									color          : '#666',
									padding        : '4px 8px',
									borderRadius   : '4px',
									display        : 'flex',
									alignItems     : 'center',
									justifyContent : 'center'
								}}
								title="Próximo mês"
							>
								›
							</button>
						</div>
						
						<div style={{ 
							display             : 'grid', 
							gridTemplateColumns : 'repeat(7, 1fr)', 
							gap                 : '2px',
							marginBottom        : '8px'
						}}>
							{[ 'Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb' ].map( day => (
								<div key={day} style={{ 
									textAlign  : 'center', 
									fontSize   : '12px', 
									fontWeight : '600',
									color      : '#666',
									padding    : '8px 4px'
								}}>
									{day}
								</div>
							) )}
						</div>
						
						<div style={{ 
							display             : 'grid', 
							gridTemplateColumns : 'repeat(7, 1fr)', 
							gap                 : '2px'
						}}>
							{calendarDays.map( ( date, index ) => {
								const isCurrentMonth = date.getMonth() === currentMonth;
								const isSelected = ( tempStartDate && date.getTime() === tempStartDate.getTime() ) || 
												 ( tempEndDate && date.getTime() === tempEndDate.getTime() );
								const isInCurrentRange = isInRange( date );
								
								return (
									<DateButton
										key={index}
										onClick={() => isCurrentMonth && handleDateClick( date )}
										$isCurrentMonth={isCurrentMonth}
										$isSelected={isSelected}
										$isInRange={isInCurrentRange}
									>
										{date.getDate()}
									</DateButton>
								);
							} )}
						</div>
						
						<div style={{ 
							display        : 'flex', 
							justifyContent : 'space-between', 
							marginTop      : '16px',
							gap            : '8px'
						}}>
							<button
								onClick={handleClear}
								style={{
									padding         : '8px 16px',
									border          : '1px solid #ddd',
									backgroundColor : '#fff',
									color           : '#666',
									borderRadius    : '4px',
									cursor          : 'pointer',
									fontSize        : '14px'
								}}
							>
								Limpar
							</button>
							<button
								onClick={handleApply}
								disabled={!tempStartDate || !tempEndDate}
								style={{
									padding         : '8px 16px',
									border          : 'none',
									backgroundColor : tempStartDate && tempEndDate ? '#007bff' : '#ccc',
									color           : '#fff',
									borderRadius    : '4px',
									cursor          : tempStartDate && tempEndDate ? 'pointer' : 'not-allowed',
									fontSize        : '14px'
								}}
							>
								Aplicar
							</button>
						</div>
					</Calendar>
				</Dropdown>
			)}
		</Container>
	);
}; 